import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { GlobalService } from 'src/app/app-core/lib/global.service';
import { DocumentService } from 'src/app/app-core/services/document.service';
import { LoginService } from 'src/app/app-core/services/login.service';
import { ProcessService } from 'src/app/app-core/services/process.service';
import { RequestService } from 'src/app/app-core/services/request.service';

type ConsultaTab = 'protocolo' | 'documento';

interface FaqItem {
  pergunta: string;
  resposta: string;
  isAlternativeLogin?: boolean;
}

@Component({
  selector: 'app-home-page',
  templateUrl: './home-page.component.html',
  styleUrls: ['./home-page.component.scss'],
  providers: [LoginService, DocumentService, ProcessService, RequestService]
})
export class HomePageComponent implements OnInit {

  public searchValue: string;
  public navbarOpen: boolean = false;
  public loadScreen = true;
  public authKeyDocument: string;
  public readonly ano = new Date().getFullYear();

  public consultaTab: ConsultaTab = 'protocolo';
  public faqAberta: number | null = 0;

  // Login alternativo
  public showAlternativeLoginForm: boolean = false;
  public cpfAlternativo: string = '';
  public senhaAlternativa: string = '';

  public readonly abasConsulta: { id: ConsultaTab; label: string }[] = [
    { id: 'protocolo', label: 'Acompanhar protocolo' },
    { id: 'documento', label: 'Validar documento' },
  ];

  public readonly menu = [
    { id: 'servicos', label: 'Serviços' },
    { id: 'como-funciona', label: 'Como funciona' },
    { id: 'duvidas', label: 'Dúvidas' },
    { id: 'contato', label: 'Fale conosco' },
  ];

  public readonly servicos = [
    { nome: 'Inscrição municipal e expedição de alvará', orgao: 'Secretaria de Finanças, Planejamento e Orçamento' },
    { nome: 'Alteração cadastral e regularização de alvará', orgao: 'Secretaria de Finanças, Planejamento e Orçamento' },
    { nome: 'Comprovação de pagamento de DAM', orgao: 'Secretaria de Finanças, Planejamento e Orçamento' },
    { nome: 'Negociação de débitos em dívida ativa ISS', orgao: 'Secretaria de Finanças, Planejamento e Orçamento' },
    { nome: 'Processo seletivo', orgao: 'Secretaria Municipal de Educação' },
    { nome: 'Afastamento para desempenho de mandato eletivo', orgao: 'Secretaria de Administração' },
  ];

  public readonly passos = [
    { titulo: 'Entre com gov.br', texto: 'Login rápido e seguro com as suas credenciais do gov.br.' },
    { titulo: 'Escolha o serviço', texto: 'Busque por uma palavra, como “alvará” ou “IPTU”, e veja prazo e documentos.' },
    { titulo: 'Anexe e envie', texto: 'Revise o resumo, assine digitalmente e receba o número do protocolo na hora.' },
    { titulo: 'Acompanhe', texto: 'Receba avisos quando o pedido mudar de etapa ou precisar de algo seu.' },
  ];

  public readonly faq: FaqItem[] = [
    {
      pergunta: 'Como entrar no sistema?',
      resposta: 'Clique em “Entrar com gov.br”, no topo da página, e use o CPF e a senha da sua conta gov.br. Se ainda não tiver conta, crie gratuitamente no <a href="https://www.gov.br/pt-br/servicos/criar-sua-conta-gov.br" target="_blank" rel="noopener">site gov.br</a>.',
    },
    {
      pergunta: 'Quem pode utilizar o ProtocolizeSGA?',
      resposta: 'Cidadãos, empresas e servidores que precisem fazer uma solicitação à Prefeitura de São Gonçalo do Amarante. Basta ter uma conta gov.br.',
    },
    {
      pergunta: 'Quais assuntos consigo tratar no ProtocolizeSGA?',
      resposta: 'Pedidos às secretarias municipais, como alvarás, comprovação de pagamento de DAM, negociação de débitos e processos seletivos. A lista completa aparece ao criar uma nova solicitação, depois de entrar.',
    },
    {
      pergunta: 'O que é um processo?',
      resposta: 'Quando a solicitação é aceita para análise, ela vira um processo administrativo, com número próprio, e passa pelos setores responsáveis até a decisão final.',
    },
    {
      pergunta: 'Como validar meu processo ou solicitação?',
      resposta: 'Na consulta rápida, no topo da página: use “Acompanhar protocolo” para ver o status do pedido, ou “Validar documento” para confirmar a autenticidade de um documento pela chave de verificação impressa nele.',
    },
    {
      pergunta: 'Minha solicitação precisa de correção, e agora?',
      resposta: 'Entre no sistema e abra “Minhas solicitações”, na aba “Precisam de você”. Veja o que foi pedido, corrija ou anexe o documento e reenvie dentro do prazo indicado.',
    },
    {
      pergunta: 'Sou servidor e não consigo acessar via Gov.br, o que fazer?',
      resposta: 'Em situações emergenciais, servidores podem acessar o sistema utilizando CPF e senha temporária gerada pelo administrador.',
      isAlternativeLogin: true,
    },
  ];

  constructor(
    private service: LoginService,
    private route: ActivatedRoute,
    private global: GlobalService,
    private documentService: DocumentService,
    private processService: ProcessService,
    private requestService: RequestService,
  ) {
    const decodedPath = decodeURIComponent(window.location.href.split('redirect=')[1]);
    const queryString = decodedPath.split('?')[1];
    const queryParams = this.global.decodeQueryString(queryString);

    if (queryParams.code) {
      this.loadScreen = false;
      this.global.startLoading();
      const code = queryParams.code;
      this.service.authentication(true, code);
      this.global.stopLoading();
    }

    this.route.queryParams.subscribe((params) => {
      if (params.logout == 'true') {
        this.service.logout();
      }
    });
  }

  ngOnInit(): void {
    document.querySelector('title').text = 'Bem-vindo ao Protocolize SGA';
  }

  public entrarComSenhaTemporaria() {
    if (!this.cpfAlternativo || !this.senhaAlternativa) {
      this.global.swal.fire('Atenção', 'Informe o CPF e a senha.', 'warning');
      return;
    }
    this.service.loginAlternativo(this.cpfAlternativo, this.senhaAlternativa);
  }

  public search() {
    this.global.router.navigate(['/search-process'], { queryParams: { searchValue: this.searchValue } });
  }

  toggleNavbar() {
    this.navbarOpen = !this.navbarOpen;
  }

  public toggleFaq(index: number): void {
    this.faqAberta = this.faqAberta === index ? null : index;
  }

  public navClick(section: string, event?: Event) {
    event?.preventDefault();
    this.navbarOpen = false;
    document.getElementById(section)?.scrollIntoView({ behavior: 'smooth' });
  }

  public validarDocumento() {
    if (this.authKeyDocument?.length !== 12) {
      this.global.swal.fire({
        icon: 'error',
        title: 'Chave inválida!',
        text: 'A chave de verificação tem 12 números e fica no final do documento.',
      });
      return;
    }

    this.global.startLoading();
    this.documentService.getVericityOfDocument(this.authKeyDocument).subscribe({
      next: (response: any) => {
        this.global.stopLoading();
        this.global.swal.fire(response.swal).then(({ isConfirmed }) => {
          if (isConfirmed && response.ok) {
            this.abrirDocumentoValidado(response.type, response.entity);
          }
        });
      },
      error: () => {
        this.global.stopLoading();
        this.global.swal.fire({
          icon: 'error',
          title: 'Não foi possível validar agora',
          text: 'Tente novamente em alguns instantes.',
        });
      },
    });
  }

  private abrirDocumentoValidado(type: string, entity: any) {
    if (type === 'documento') {
      this.global.swal.fire({ title: entity.DOC_Assunto, html: entity.DOC_Conteudo });
    } else if (type === 'solicitacao') {
      this.requestService.generateRequestPDF(entity);
    } else if (type === 'processo') {
      this.processService.generateProcessPDF(entity);
    }
  }
}
